import React from "react";
import {
  Box,
  Divider,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  InputAdornment,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import SearchIcon from "@mui/icons-material/Search";
import SwapHoriz from "@mui/icons-material/SwapHoriz";
import SchoolIcon from "@mui/icons-material/School";
import { format, startOfToday, isBefore } from "date-fns";
import VersionBadgeAndReload from "./VersionBadgeAndReload";
import { FlightMenutItem } from "../../FlightMenuItem";

const FlightsPanel = ({
  search,
  setSearch,
  visibleGroups,
  expandedDates,
  handleAccordionChange,
  expandAll,
  collapseAll,
  listRootRef,
  isCabinCrew,
  isTrainingMode,
  onTrainingModeChange,
  selectedFlightId,
  changeFlight,
  changeCrewType,
  shouldRefresh,
  isReloading,
  clearCacheAndReload,
  setOpen,
  calcStopTime,
  appVersion,
}) => {
  return (
    <Box
      sx={{
        width: 280,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box sx={{ p: 1, display: "flex", alignItems: "center" }}>
        <TextField
          fullWidth
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search flights"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
        {search ? (
          <Button size="small" onClick={() => setSearch("")}>
            Clear
          </Button>
        ) : null}
        <Tooltip title="Expand all">
          <span>
            <IconButton
              aria-label="Expand all"
              size="small"
              onClick={expandAll}
            >
              <UnfoldMoreIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Collapse all">
          <span>
            <IconButton
              aria-label="Collapse all"
              size="small"
              onClick={collapseAll}
            >
              <UnfoldLessIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box ref={listRootRef} sx={{ flex: 1, overflowY: "auto", pt: 2 }}>
        {Object.keys(visibleGroups).length === 0 ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: 2,
              py: 4,
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No flights match your search
              </Typography>
              {search ? (
                <Button
                  sx={{ mt: 1 }}
                  size="small"
                  onClick={() => setSearch("")}
                >
                  Clear filter
                </Button>
              ) : null}
            </Box>
          </Box>
        ) : (
          Object.keys(visibleGroups)
            .sort((leftKey, rightKey) => {
              if (isTrainingMode) {
                // Sort simulator blocks alphabetically
                return leftKey.localeCompare(rightKey);
              } else {
                // Sort by date
                return (
                  new Date(visibleGroups[leftKey]?.[0]?.departureDateTime) -
                  new Date(visibleGroups[rightKey]?.[0]?.departureDateTime)
                );
              }
            })
            .map((groupKey, groupIndex) => {
              const firstFlightInGroup = visibleGroups[groupKey]?.[0];

              const groupLabel = isTrainingMode
                ? groupKey
                : firstFlightInGroup?.departureDateTime
                ? format(
                    new Date(firstFlightInGroup.departureDateTime),
                    "dd MMM yyyy"
                  )
                : "N/A";

              const groupFlightCount = visibleGroups[groupKey]?.length || 0;
              const isExpanded = !!expandedDates[groupKey];
              const firstDepartureDate = firstFlightInGroup?.departureDateTime
                ? new Date(firstFlightInGroup.departureDateTime)
                : null;
              const isPastDateGroup = firstDepartureDate
                ? isBefore(firstDepartureDate, startOfToday())
                : false;
              return (
                <Box key={groupKey}>
                  <Divider />
                  <Accordion
                    expanded={isExpanded}
                    onChange={handleAccordionChange(groupKey)}
                    disableGutters
                    sx={{ boxShadow: "none" }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls={`group-${groupIndex}-content`}
                      id={`group-${groupIndex}-header`}
                      disableRipple
                      disableTouchRipple
                      sx={{
                        outline: "none",
                        "&.Mui-focusVisible": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          width: "100%",
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color:
                              isPastDateGroup && !isTrainingMode
                                ? "text.disabled"
                                : "text.secondary",
                          }}
                        >
                          {groupLabel.toUpperCase()}
                        </Typography>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Chip label={groupFlightCount} size="small" />
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 0 }}>
                      {visibleGroups[groupKey].map(
                        (flight, flightIndex, flightsInGroup) => {
                          const destinationLabel = isCabinCrew
                            ? flight?.sDest || "N/A"
                            : flight?.sDestICAO || "N/A";

                          const nextDepartureDateTime =
                            flightsInGroup?.[flightIndex + 1]
                              ?.departureDateTime;

                          const arrivalDateTime = flight?.arrivalDateTime;

                          const stopDurationLabel =
                            nextDepartureDateTime && arrivalDateTime
                              ? calcStopTime(
                                  nextDepartureDateTime,
                                  arrivalDateTime
                                )
                              : "N/A";

                          const hasNextFlight = Boolean(
                            flightsInGroup?.[flightIndex + 1]
                          );

                          return (
                            <Box key={`${groupKey}-${flightIndex}`}>
                              <FlightMenutItem
                                flightKey={`flight-${flightIndex}`}
                                changeFlight={(id) => {
                                  changeFlight?.(id);
                                  setOpen(false);
                                }}
                                toggleSlideOut={() => setOpen(false)}
                                isCabinCrew={isCabinCrew}
                                flight={flight}
                                isSelected={selectedFlightId === flight?.id}
                              />
                              {hasNextFlight && !isTrainingMode && (
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    my: 1,
                                    mx: 1,
                                    color: "text.secondary",
                                    fontStyle: "italic",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      flex: 1,
                                      height: 1,
                                      bgcolor: "divider",
                                    }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{ whiteSpace: "nowrap" }}
                                  >
                                    {destinationLabel} stop{" "}
                                    <Typography
                                      component="span"
                                      color="error.main"
                                      variant="caption"
                                    >
                                      {stopDurationLabel}
                                    </Typography>
                                  </Typography>
                                  <Box
                                    sx={{
                                      flex: 1,
                                      height: 1,
                                      bgcolor: "divider",
                                    }}
                                  />
                                </Box>
                              )}
                            </Box>
                          );
                        }
                      )}
                    </AccordionDetails>
                  </Accordion>
                </Box>
              );
            })
        )}
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box
        sx={{
          px: 2,
          py: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant="caption"
          color={shouldRefresh ? "error" : "text.secondary"}
        >
          {shouldRefresh
            ? "New version available"
            : `Latest version v${appVersion}`}
        </Typography>
        <VersionBadgeAndReload
          shouldRefresh={shouldRefresh}
          isReloading={isReloading}
          clearCacheAndReload={clearCacheAndReload}
        />
      </Box>

      {visibleGroups && (
        <>
          <Divider sx={{ mt: 1 }} />
          <Box sx={{ p: 2, pt: 1 }}>
            <Button
              fullWidth
              variant="contained"
              color={"primary"}
              size="medium"
              startIcon={<SchoolIcon />}
              disableElevation
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                mb: 1,
              }}
              aria-label={
                isTrainingMode ? "Exit Training Mode" : "Enter Training Mode"
              }
              onClick={() => {
                onTrainingModeChange?.();
                setOpen(false);
              }}
            >
              {isTrainingMode ? "Exit Training" : "Training"}
            </Button>
            {!isTrainingMode && (
              <Button
                fullWidth
                variant="contained"
                color="error"
                size="medium"
                startIcon={<SwapHoriz />}
                disableElevation
                sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
                aria-label={
                  isCabinCrew ? "Switch to Pilot Info" : "Switch to Cabin Info"
                }
                onClick={() => {
                  changeCrewType?.();
                  setOpen(false);
                }}
              >
                {isCabinCrew ? "Pilot Info" : "Cabin Info"}
              </Button>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

export default FlightsPanel;
export { FlightsPanel };
